import { useEffect, useMemo, useRef } from 'react';
import { EditorContent, ReactRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import type { DynVarOption } from '../hooks/useDynamicVariables';
import {
  agentInstructionsToHTML,
  agentInstructionsFromEditor,
} from './agentInstructionsSerialization';
import {
  AgentVarSuggestionList,
  type AgentVarSuggestionListRef,
} from './AgentVarSuggestionList';
import { LoadingIcon } from './LoadingIcon';

type Props = {
  value?: string;
  onChange?: (val: string) => void;
  dynVarOptions: DynVarOption[];
  maxOptions?: number;
  rows?: number;
  isGenerating?: boolean;
};

// The box's height comes from CSS classes (`agentVarEditorWrap--rows-N`) for
// the row counts BehaviorSection uses; any other value falls back to 13 rows.
const ROWS_CLASS: Record<number, string> = {
  4: 'agentVarEditorWrap--rows-4',
  6: 'agentVarEditorWrap--rows-6',
  12: 'agentVarEditorWrap--rows-12',
  13: 'agentVarEditorWrap--rows-13',
};

const isDynVarOption = (v: unknown): v is DynVarOption =>
  typeof v === 'object' && v !== null && 'value' in v && 'label' in v;

export function filterDynVarOptions(
  options: DynVarOption[],
  query: string,
  maxOptions: number
): DynVarOption[] {
  const q = query.trim().toLowerCase();

  const seen = new Set<string>();
  return options
    .filter((o) =>
      String(o.value || '')
        .toLowerCase()
        .startsWith(q)
    )
    .filter((o) => {
      const k = String(o.value || '').toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .slice(0, maxOptions);
}

/**
 * Renders agent instructions as rich text: recognized `{token}` variables
 * show as rounded mention chips (matching the Logiblocks email node's
 * variable pills) instead of raw curly-brace text.
 */
export const AgentVariableEditor: React.FC<Props> = ({
  value = '',
  onChange,
  dynVarOptions,
  maxOptions = 12,
  rows = 4,
  isGenerating = false,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const dynVarOptionsRef = useRef(dynVarOptions);
  useEffect(() => {
    dynVarOptionsRef.current = dynVarOptions;
  }, [dynVarOptions]);

  // Tracks which dynVarOptions reference the editor's content was last
  // decorated with, so we can force a re-decorate when it changes (e.g.
  // dynamic variables finish loading after mount) without disrupting
  // in-progress typing on every keystroke.
  const appliedOptionsRef = useRef<DynVarOption[] | null>(null);

  const initialHTML = useMemo(
    () => agentInstructionsToHTML(value, dynVarOptionsRef.current),
    // Only compute once on mount — external syncs are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bold: false,
        italic: false,
        strike: false,
        code: false,
        codeBlock: false,
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: 'How the agent should behave and handle scenarios',
      }),
      Mention.configure({
        renderHTML: ({ node }) => [
          'span',
          {
            'data-type': 'mention',
            'data-id': node.attrs.id,
            'data-label': node.attrs.label,
            title: node.attrs.label,
          },
          node.attrs.label ?? node.attrs.id,
        ],
        suggestion: {
          char: '{',
          allowedPrefixes: null,
          items: ({ query }) =>
            filterDynVarOptions(dynVarOptionsRef.current, query, maxOptions),
          command: ({ editor: ed, range, props }) => {
            // The suggestion list passes the selected DynVarOption through
            // as `props`, though Mention types it as its own node attrs.
            if (!isDynVarOption(props)) return;
            const item = props;
            ed.chain()
              .focus()
              .insertContentAt(range, [
                {
                  type: 'mention',
                  attrs: { id: item.value, label: item.label },
                },
                { type: 'text', text: ' ' },
              ])
              .run();
          },
          render: () => {
            let component: ReactRenderer<AgentVarSuggestionListRef>;
            let popup: ReturnType<typeof tippy> | undefined;

            return {
              onStart: (props) => {
                component = new ReactRenderer(AgentVarSuggestionList, {
                  props,
                  editor: props.editor,
                });
                if (!props.clientRect) return;
                popup = tippy('body', {
                  getReferenceClientRect: () => props.clientRect?.() as DOMRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                });
              },
              onUpdate: (props) => {
                component.updateProps(props);
                if (!props.clientRect) return;
                popup?.[0]?.setProps({
                  getReferenceClientRect: () => props.clientRect?.() as DOMRect,
                });
              },
              onKeyDown: (props) => {
                if (props.event.key === 'Escape') {
                  popup?.[0]?.hide();
                  return true;
                }
                return component.ref?.onKeyDown(props) ?? false;
              },
              onExit: () => {
                popup?.[0]?.destroy();
                component.destroy();
              },
            };
          },
        },
      }),
    ],
    content: initialHTML,
    onUpdate: ({ editor: ed }) => {
      onChange?.(agentInstructionsFromEditor(ed));
    },
  });

  // Sync editor content when the external value or the resolvable variable
  // list changes (e.g. dynamic variables finish loading after mount).
  useEffect(() => {
    if (!editor) return;
    const current = agentInstructionsFromEditor(editor);
    const optionsChanged = appliedOptionsRef.current !== dynVarOptions;
    if (current !== value || optionsChanged) {
      editor.commands.setContent(
        agentInstructionsToHTML(value, dynVarOptionsRef.current),
        { emitUpdate: false }
      );
      appliedOptionsRef.current = dynVarOptions;
    }
  }, [editor, value, dynVarOptions]);

  // The loading overlay is centred in this box, so bring the box back to the
  // top (and lock it, below) while generating - otherwise a scrolled box would
  // leave the spinner out of view.
  useEffect(() => {
    if (isGenerating && wrapRef.current) wrapRef.current.scrollTop = 0;
  }, [isGenerating]);

  return (
    <div
      ref={wrapRef}
      className={`agentVarEditorWrap ${ROWS_CLASS[rows] ?? ROWS_CLASS[13]} ${
        isGenerating ? 'agentVarEditorWrap--generating' : ''
      }`}
      onMouseDown={(e) => {
        // The contentEditable ProseMirror element only grows to fit its
        // text, while this wrapper is held taller by `rows` - clicking the
        // padded area below the last line lands on the wrapper itself, not
        // on editable content, so it wouldn't otherwise focus anything.
        if (!(e.target as HTMLElement).closest('.ProseMirror')) {
          e.preventDefault();
          // The box is already visible (the user just clicked inside it),
          // so there's nothing to scroll into view.
          editor?.commands.focus('end', { scrollIntoView: false });
        }
      }}
    >
      <EditorContent editor={editor} />
      {isGenerating && (
        <div className='agentVarEditorLoadingOverlay'>
          <LoadingIcon />
        </div>
      )}
    </div>
  );
};

export default AgentVariableEditor;
